// Phase 17 Plan 04 Task 2 — PhaseControls primitive (UI-SPEC §5.1 row 9).
//
// Match-screen Phase A picker — a single <Seg> radiogroup inside a <section>
// labelled by the localized match.phase_controls.label message (i18n-routed via
// useIntl().formatMessage at render time).
//
// Phase A options: word | skip  (Phase 24 RUNE-05 deleted 'swap')
//
// ARCA-619 / ARCA-715 — the Phase B (chess) segment radiogroup was REMOVED.
// The chess step is now COMPOSITE (stage any combination of move / empower /
// powerup, no mutually-exclusive pick, no Pass) and is driven by its own
// control deck (ChessControlDeck) + the board surfaces, not a Seg. PhaseControls
// is therefore Phase-A-only.
//
// disabledA is filtered OUT of the options list (Phase 17 implementation; a
// future v2 may render aria-disabled radios with reduced opacity per UI-SPEC
// §6.3 row 5b — out of scope here).
//
// Null phaseA falls back to the first non-disabled option (so the Seg always
// has a defined `value` per its API contract).
//
// POLISH-02: the Sun-Yellow selected-segment body resolves via the
// `.seg button[aria-checked="true"]` recipe in pieces.css (Plan 17-03
// extended the recipe to match aria-checked alongside aria-selected).

import { type CSSProperties, type JSX, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { Seg, type SegOption } from './Seg'

export type PhaseAValue = 'word' | 'skip'

// POWERUP-05 / EMPOWER-05 — segment labels route through the i18n catalog
// (match.phase_a.*) instead of the prior hardcoded English literals, so Swedish
// players see "HOPPA ÖVER" not "SKIP". The SCREAMING-caps look is preserved
// purely in CSS (`.phase-controls .seg button` text-transform in pieces.css) —
// never JS .toUpperCase() on a localized string, which is locale-unsafe.
// value↔i18n-key pairs; labels resolved at render with intl.
const PHASE_A_OPTION_KEYS: ReadonlyArray<{
  value: PhaseAValue
  key: string
}> = [
  { value: 'word', key: 'match.phase_a.word' },
  { value: 'skip', key: 'match.phase_a.skip' },
]

export interface PhaseControlsProps {
  readonly phaseA: PhaseAValue | null
  readonly setPhaseA: (v: PhaseAValue) => void
  readonly disabledA?: ReadonlyArray<PhaseAValue>
  readonly style?: CSSProperties
  /**
   * Increment to retrigger a brief enlarge+bounce animation on the seg. Used by
   * MatchScreen when Submit is pressed without a required phase set — the view
   * switches and the seg bounces to draw attention.
   */
  readonly bounceKey?: number
}

export function PhaseControls({
  phaseA,
  setPhaseA,
  disabledA = [],
  style,
  bounceKey = 0,
}: PhaseControlsProps): JSX.Element {
  const intl = useIntl()
  const containerLabel = intl.formatMessage({
    id: 'match.phase_controls.label',
  })
  const phaseALabel = intl.formatMessage({ id: 'match.phase_a.label' })

  const optionsA: ReadonlyArray<SegOption<PhaseAValue>> =
    PHASE_A_OPTION_KEYS.filter((o) => !disabledA.includes(o.value)).map(
      (o) => ({ value: o.value, label: intl.formatMessage({ id: o.key }) }),
    )

  const effectiveA: PhaseAValue = phaseA ?? optionsA[0]?.value ?? 'word'

  const [bouncing, setBouncing] = useState(false)
  useEffect(() => {
    if (bounceKey <= 0) return
    setBouncing(true)
    const t = window.setTimeout(() => setBouncing(false), 600)
    return () => {
      window.clearTimeout(t)
    }
  }, [bounceKey])

  return (
    <section
      className={`phase-controls${bouncing ? ' bouncing' : ''}`}
      aria-label={containerLabel}
      style={style}
    >
      <Seg<PhaseAValue>
        options={optionsA}
        value={effectiveA}
        onChange={setPhaseA}
        ariaLabel={phaseALabel}
        testId="phase-a-seg"
      />
    </section>
  )
}
